import { Component, inject, OnDestroy } from '@angular/core';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { EventViewModel } from '../../models-views/event.view.model';
import { BehaviorSubject, debounceTime, Observable, Observer, Subject, takeUntil } from 'rxjs';
import { VendorProductModal, VendorSubmissionAnswerReponseModel, VendorSubmissionModel } from '../../models/vendor.model';
import { OutsourcingFormQuestionResponseModel, OutsourcingFormSectionResponseModel } from '../../models/outsourcing-form.model';
import LoginModel from '../../models/user.model';
import { AppCommon } from '../../providers/app-common';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from '../../../environments/environment';
import { PptGenService } from '../../services/ppt-gen-services/ppt-gen.ppt.service';
import { DocxGenService } from '../../services/ppt-gen-services/ppt-gen.docx.service';
import { getCurrencySymbol } from '@angular/common'; // Import getCurrencySymbol

interface IModalData {
  eventId: any;
  submissionId: any;
}

@Component({
  selector: 'app-vendor-submission-modal',
  standalone: false,
  templateUrl: './vendor-submission-modal.component.html',
  styleUrl: './vendor-submission-modal.component.scss'
})
export class VendorSubmissionModalComponent implements OnDestroy {

  readonly nzModalData: IModalData = inject(NZ_MODAL_DATA);
  vendorSubmissionModel = new BehaviorSubject<VendorSubmissionModel>(
    new VendorSubmissionModel()
  );
  vendorSubmissionAnswerReponseModel = new BehaviorSubject<VendorSubmissionAnswerReponseModel>(
    new VendorSubmissionAnswerReponseModel()
  );
  outsourcingFormSectionResponseModel = new BehaviorSubject<OutsourcingFormSectionResponseModel>(
    new OutsourcingFormSectionResponseModel()
  );

  outsourcingFormQuestionResponseModel = new BehaviorSubject<OutsourcingFormQuestionResponseModel>(
    new OutsourcingFormQuestionResponseModel()
  );
  loading:boolean = false;
  loggedInUser: any;
  sectionsData: any[] = [];
  formId: string | null = null;
  isLoading = false;
  loginModel: LoginModel | null = null;
  isFinalized = false;
  isShortlisted = false;
  loadingConversion: boolean = false;
  form: FormGroup;
  destroy$ = new Subject<void>();
  currencyTypes: any[] = [];
  taxOptions: any[] = [];
  fileList: NzUploadFile[] = [];
  loadingMsg = new BehaviorSubject('')

  // NEW: Edit mode tracking for Q&A sections
  editingQuestionId: number | null = null;
  editedAnswerValues: { [key: number]: any } = {};

  constructor(private vm: EventViewModel, private fb: FormBuilder, private message: NzMessageService,
    private pptGenService: PptGenService,
    private docxGenService: DocxGenService
  ) {
    this.vm.vendorSubmissionModalComponent = this;
    this.loginModel = AppCommon.getloggedInObject();
    this.loggedInUser = AppCommon.getloggedInObject().user;

    this.form = this.fb.group({
      currency: [null, Validators.required],
      file: [[]],
      exchange_rate: [1, [Validators.required, Validators.min(0.0001)]],
      usd_amount: [{ value: 0, disabled: true }],
      product: this.fb.array([]),
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.calculate());
    this.vm.currencyList.pipe(takeUntil(this.destroy$)).subscribe(e => this.currencyTypes = e.results);
    this.vm.vendorTaxResponseModel.pipe(takeUntil(this.destroy$)).subscribe(e => this.taxOptions = e.results);
    this.getCurrencyChange();
    this.getAmountPaidUSDOnExchangeRateChange();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.vm.parentVendorSubmissionModalComponent = this;
    this.vm.subscribeToVendorSubmission(
      this.vendorSubmissionModel,
      this.outsourcingFormSectionResponseModel,
      this.outsourcingFormQuestionResponseModel,
      this.vendorSubmissionAnswerReponseModel
    );
    this.vm.getVendorSubmissionByID(this.nzModalData.submissionId);
    this.vm.getCurrencyList(1, 500);
    this.vm.getVendorTaxList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }



  get itemRows(): FormArray {
    return this.form.get('product') as FormArray;
  }

  createItemRow(data?:any): FormGroup {
    return this.fb.group({
      id: [data?.id || null],
      product: [data?.product || '', Validators.required],
      amount_before_tax: [+data?.amount_before_tax || 0, [Validators.required, Validators.min(0)]],
      tax: [data?.tax || [], Validators.required],
      total_price: [{ value: +data?.total_price || 0, disabled: true }]
    });
  }

  addItemRow(data:any): void {
    const newItem = this.createItemRow(data);
    newItem.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500)).subscribe(() => {
      this.calculateItemTotalPrice(newItem);
      this.updateItemRow(newItem.getRawValue());
    });
    this.itemRows.push(newItem);
    this.calculateItemTotalPrice(newItem);
    this.calculate();
  }

  addRow(){
    let data:Partial<VendorProductModal> = {
      product:'Product' +' '+(this.itemRows.length + 1),
      amount_before_tax	:0,
      total_price:0,
      created_by: this.loginModel.user.id,
      submission:this.nzModalData.submissionId,
      tax:[]
    }
    this.loadingMsg.next('Adding vendor pricing...')
    this.loading = true
    this.vm.createVendorProduct(data)
  }

  productCreated(data:any){
    // console.log('[productCreated] Product created data received from parent:', data);
    this.loading =  false
    this.addItemRow(data);
  }

  formUpdated(){
    // console.log('[formUpdated] Form update confirmation received from parent');
    this.loading =  false;
    this.message.success('Pricing section quotations saved successfully.');
  }

  removeItemRow(index: number, e:any): void {
    e.preventDefault();
    this.loading = true
    this.loadingMsg.next('Deleting vendor pricing...')
    this.vm.deleteVendorProduct(this.itemRows.at(index).value,index)
  }

  updateItemRow(data: any): void {
    this.loadingMsg.next('Updating vendor pricing...')
    this.loading = true
    this.vm.updateVendorProduct(data)
  }

  productDeleted(index:any){
    // console.log('[productDeleted] Product deletion confirmation received from parent. Index:', index);
    this.itemRows.removeAt(index);
    this.calculate();
    this.loading = false;
  }

  calculateItemTotalPrice(itemFormGroup: FormGroup): void {
    const { amount_before_tax, tax } = itemFormGroup.getRawValue();
    let totalTaxRate = 0;

    (tax || []).forEach((taxId: string | number) => {
      const selectedTax = this.taxOptions.find((taxOption) => String(taxOption.id) === String(taxId));
      if (selectedTax) {
        totalTaxRate += (selectedTax.rate / 100);
      }
    });

    if (amount_before_tax != null || (tax || []).length > 0) {
        const baseAmount = Number(amount_before_tax) || 0;
        const taxAmount = baseAmount * totalTaxRate;
        const total_price_per_item = baseAmount + taxAmount;

        itemFormGroup.patchValue({
          total_price: total_price_per_item.toFixed(2),
        }, { emitEvent: false });
        this.calculate();
    }

  }

  // New method to calculate the sum of all item total prices
  getTotalPriceForAllItems(): number {
    let overallTotalPrice = 0;
    this.itemRows.controls.forEach(itemFormGroup => {
      const itemTotalPrice = itemFormGroup.get('total_price')?.value;
      if (itemTotalPrice) {
        overallTotalPrice += parseFloat(itemTotalPrice);
      }
    });
    return overallTotalPrice;
  }

  // New method to get the selected currency symbol
  getCurrencySymbol(): string {
    const selectedCurrencyId = this.form.get('currency')?.value;
    if (!selectedCurrencyId) {
      return '';
    }
    const selectedCurrency = this.currencyTypes.find(c => c.id === selectedCurrencyId);
    return selectedCurrency ? getCurrencySymbol(selectedCurrency.symbol, 'narrow') : '$'; // 'code' might be 'USD', 'EUR', etc.
  }


  calculate(): void {
    const overallTotalPrice = this.getTotalPriceForAllItems(); // Use the new getter
    const exchange_rate = this.form.get('exchange_rate')?.value;
    const usd_amount = exchange_rate ? overallTotalPrice / exchange_rate : 0;

    this.form.patchValue(
      {
        usd_amount: usd_amount.toFixed(2)
      },
      { emitEvent: false }
    );
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loadingMsg.next('Updating vendor pricing...')
      this.loading = true
      const payload = this.form.getRawValue();
      delete payload.product

      payload.modified_by = this.loggedInUser.id;
      payload.vendor = this.vendorSubmissionModel.value.quotation_request_vendor_details.vendor;
      this.vm.updateVendorSubmissionByID(payload, this.nzModalData.submissionId);
    }
  }

  vendorSubmissionRecieved() {
    // console.log('[vendorSubmissionRecieved] Data received from parent:', this.vendorSubmissionModel.value);
    this.formId = this.vendorSubmissionModel.value.quotation_request_vendor_details.quotation_request_details.outsourcing_form;
    this.isShortlisted = this.vendorSubmissionModel.value.quotation_request_vendor_details.is_shortlisted;
    this.isFinalized = this.vendorSubmissionModel.value.quotation_request_vendor_details.isFinalized;

    let d = {
      currency: this.vendorSubmissionModel.value?.currency,
      exchange_rate: this.vendorSubmissionModel.value?.exchange_rate,
      usd_amount: this.vendorSubmissionModel.value?.usd_amount,
    }

    // Instead of mapping and patching, use the existing addItemRow method:
    if(this.vendorSubmissionModel.value?.product_details != null && Array.isArray(this.vendorSubmissionModel.value?.product_details)) {
      const productDetails = this.vendorSubmissionModel.value?.product_details || [];
      productDetails.forEach((item: any) => this.addItemRow(item));
    }


  this.form.patchValue(d, { emitEvent: false });



    this.vendorSubmissionModel.value?.file_details?.length > 0 && (this.fileList = this.vendorSubmissionModel.value?.file_details.map(file => ({
      uid: file.id,
      name: file.name,
      status: 'done',
      url: `${environment.api_url}${file.file_url}`
    })));
    this.vm.getSections(this.formId, 'vendor-submission-modal');
  }

  sectionsDataReceived() {
    // console.log('[sectionsDataReceived] Sections data received from parent:', this.outsourcingFormSectionResponseModel.value);
    this.sectionsData = this.outsourcingFormSectionResponseModel.value.results;
    this.sectionsData.forEach(sec => sec.questions = []);
    const sectionIds = this.sectionsData.map(el => el.id).filter(id => id != null);
    if (sectionIds.length > 0) {
      this.vm.getOSQuestions(sectionIds.join(','), 'vendor-submission-modal');
    }
  }

  questionsDataReceived() {
    // console.log('[questionsDataReceived] Questions data received from parent:', this.outsourcingFormQuestionResponseModel.value);
    this.outsourcingFormQuestionResponseModel.value.results.forEach(element => {
      const section = this.sectionsData.find(el => el.id === element.section);
      if (section) {
        section.questions.push(element);
      }
    });
    this.vm.getVendorAnswars(1, 100, this.nzModalData.submissionId, 'vendor-submission-modal');
  }

  // NEW: Check if a question type is editable
  isAnswerEditable(questionType: string): boolean {
    const nonEditableTypes = ['FILE_UPLOAD', 'MULTIPLE_FILES'];
    return !nonEditableTypes.includes(questionType);
  }

  // NEW: Start editing a question answer
  startEdit(questionId: number, answer: any, questionType: string): void {
    // console.log('[startEdit] Starting edit mode for question:', questionId);
    this.editingQuestionId = questionId;
    
    // Store current answer value in temporary storage based on question type
    if (questionType === 'TEXT_SHORT' || questionType === 'DROPDOWN') {
      // Both TEXT_SHORT and DROPDOWN use answer_text_short field
      this.editedAnswerValues[questionId] = answer?.answer_text_short || '';
    } else if (questionType === 'TEXT_LONG') {
      this.editedAnswerValues[questionId] = answer?.answer_text_long || '';
    } else if (questionType === 'NUMBER' || questionType === 'CURRENCY') {
      // Both NUMBER and CURRENCY use answer_number field
      this.editedAnswerValues[questionId] = answer?.answer_number || 0;
    } else if (questionType === 'BOOLEAN_YES_NO') {
      this.editedAnswerValues[questionId] = answer?.answer_boolean || false;
    } else if (questionType === 'DATE') {
      this.editedAnswerValues[questionId] = answer?.answer_date || null;
    } else if (questionType === 'MULTIPLE_CHOICE_SINGLE') {
      // Stores single choice ID
      this.editedAnswerValues[questionId] = answer?.answer_selected_choice_ids || null;
    } else if (questionType === 'MULTIPLE_CHOICE_MULTI') {
      // Stores array of choice IDs
      this.editedAnswerValues[questionId] = answer?.answer_selected_choice_ids || [];
    }
  }

  // NEW: Cancel editing - discard changes
  cancelEdit(questionId: number): void {
    // console.log('[cancelEdit] Canceling edit mode for question:', questionId);
    this.editingQuestionId = null;
    delete this.editedAnswerValues[questionId];
  }

  // NEW: Update answer - save changes to parent
  updateAnswer(questionId: number, questionType: string): void {
    // console.log('[updateAnswer] Updating answer for question:', questionId, 'Type:', questionType);
    
    const newValue = this.editedAnswerValues[questionId];    const existingAnswer = this.getAnswerForQuestion(questionId);
    
    // Get answer ID for PATCH endpoint
    const answerId = existingAnswer?.id;
    
    if (!answerId) {
      this.message.error('Answer not found');
      return;
    }    
    // Create payload with mandatory fields: created_by, submission, question
    const payload: any = {
      // question: questionId,                                                    // MANDATORY: Question ID (number)
      submission: this.nzModalData.submissionId,                               // MANDATORY: Submission ID (number)
      created_by: this.vendorSubmissionModel.value.created_by,                 // MANDATORY: Created by user ID (number)
      modified_by: this.loggedInUser.id,                                       // Modified by current user (number)
    };

    // Map answer value to correct field based on question type
    // Note: Ensure data types match API specification
    if (questionType === 'TEXT_SHORT') {
      payload.answer_text_short = String(newValue || '');
    } else if (questionType === 'TEXT_LONG') {
      payload.answer_text_long = String(newValue || '');
    } else if (questionType === 'NUMBER') {
      payload.answer_number = String(newValue || '0');                        // API expects string
    } else if (questionType === 'CURRENCY') {
      payload.answer_number = String(newValue || '0');                        // API expects string
    } else if (questionType === 'BOOLEAN_YES_NO') {
      payload.answer_boolean = Boolean(newValue);                             // Ensure boolean type
    } else if (questionType === 'DATE') {
      payload.answer_date = newValue || null;                                 // ISO date string or null
    } else if (questionType === 'DROPDOWN') {
      // DROPDOWN also uses answer_text_short (stores option text)
      payload.answer_text_short = String(newValue || '');
    } else if (questionType === 'MULTIPLE_CHOICE_SINGLE') {
      // Stores single choice ID (can be number or null)
      payload.answer_selected_choice_ids = newValue || null;
    } else if (questionType === 'MULTIPLE_CHOICE_MULTI') {
      // Stores array of choice IDs (can be array or null)
      payload.answer_selected_choice_ids = newValue || [];
    }

    // console.log('[updateAnswer] Payload with mandatory fields to send:', payload);
    
    // Call parent ViewModel to update answer
    this.loading = true;
    this.loadingMsg.next('Updating answer...');
    this.vm.updateVendorAnswer(payload, 'vendor-submission-modal', answerId);
    
    // Reset edit mode
    this.editingQuestionId = null;
    delete this.editedAnswerValues[questionId];
  }

  // NEW: Get edited value for a question
  getEditedValue(questionId: number): any {
    return this.editedAnswerValues[questionId];
  }

  // OLD: getAnswerForQuestion function
  getAnswerForQuestion(questionId: number) {
    let ans: any = this.vendorSubmissionAnswerReponseModel.value.results.find(answerItem => answerItem.question === questionId);
    // console.log(ans)
    return ans;
  }

  getCurrencyChange() {
    this.form.get('currency')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      this.loadingConversion = true;
      this.vm.getconvertedCurrency('2', e, 'vendorSubmissionModalComponent');
    });
  }

  getAmountPaidUSDOnExchangeRateChange() {
    this.form.get('exchange_rate')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((exchangeRate) => {
      this.calculate();
    });
  }

  defaultAction(e: any) {
    e.preventDefault();
  }

  fetchConvertion(e: any) {
    e.preventDefault();
    if (this.form.get('currency')?.value) {
      this.loadingConversion = true;
      this.vm.getconvertedCurrency('2', this.form.get('currency').value, 'vendorSubmissionModalComponent');
    }
  }

  convertedCurrencyRecieved(e: any) {
    // console.log('[convertedCurrencyRecieved] Currency conversion data received from parent:', e);
    if (e.rate) {
      this.form.get('exchange_rate')?.setValue(e.rate.toFixed(2));
      this.loadingConversion = false;
    }
  }

  vendorAnswersRecieved() {
    // console.log('[vendorAnswersRecieved] Vendor answers data received from parent:', this.vendorSubmissionAnswerReponseModel.value);
    this.isLoading = false;
  }

  // NEW: Callback when answer is updated successfully
  vendorAnswerUpdated(updatedAnswer: any): void {
    // console.log('[vendorAnswerUpdated] Answer updated from parent:', updatedAnswer);
    
    // Find and update the answer in the local model
    const index = this.vendorSubmissionAnswerReponseModel.value.results.findIndex(
      ans => ans.question === updatedAnswer.question
    );
    
    if (index !== -1) {
      this.vendorSubmissionAnswerReponseModel.value.results[index] = updatedAnswer;
    } else {
      // If not found, add it as new answer
      this.vendorSubmissionAnswerReponseModel.value.results.push(updatedAnswer);
    }
    
    this.loading = false;
    this.message.success('Answer updated successfully');
  }

  customUploadRequest = (item: any) => {
    return this.vm.uploadMediaFile(item, this.loggedInUser.id);
  };

  onUploadFileChange(event: any): void {
    if (event.type === 'success') {
      this.form.get('file')?.setValue([...this.form.get('file').value, event.file.response.id], { emitEvent: false });
    } else if (event.type === 'error') {
      // console.error('File upload failed:', event.file);
    } else if (event.type == 'removed') {
      this.fileList = this.fileList.filter(f => f.uid !== event.file.uid);
      this.form.get('file')?.setValue([...this.fileList.map(f => f.uid)], { emitEvent: false });
    }
  }

  beforeUpload = (file: NzUploadFile, _fileList: NzUploadFile[]): Observable<boolean> =>
    new Observable((observer: Observer<boolean>) => {
      const isLt5M = file.size! / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.message.error(`${file.name} is larger than 5MB!`);
        observer.next(false);
        observer.complete();
      } else {
        observer.next(true);
        observer.complete();
      }
    });

  vendorSubmissionUpdated(type: 'Shortlisted' | 'Finalized') {
    // console.log('[vendorSubmissionUpdated] Submission update received from parent. Type:', type);
    if (type === 'Shortlisted') {
      this.isShortlisted = !this.isShortlisted;
    }

    if (type === 'Finalized') {
      this.isFinalized = !this.isFinalized;
    }
  }

  markAsShortlisted(): void {
    const payload = {
      quotation_request: this.vendorSubmissionModel.value.quotation_request_vendor_details.quotation_request,
      is_shortlisted: !this.isShortlisted,
      modified_by: this.loginModel.user.id,
      vendor: this.vendorSubmissionModel.value.quotation_request_vendor_details.vendor,
      access_token: this.vendorSubmissionModel.value.quotation_request_vendor_details.access_token
    };
    this.vm.updateOutsourcingRequestVendor(payload, this.vendorSubmissionModel.value.quotation_request_vendor, 'Shortlisted', 'vendor-submission-modal');
  }

  markAsFinalized(): void {
    const payload = {
      quotation_request: this.vendorSubmissionModel.value.quotation_request_vendor_details.quotation_request,
      is_finalized: !this.isFinalized,
      modified_by: this.loginModel.user.id,
      vendor: this.vendorSubmissionModel.value.quotation_request_vendor_details.vendor,
      access_token: this.vendorSubmissionModel.value.quotation_request_vendor_details.access_token
    };
    this.vm.updateOutsourcingRequestVendor(payload, this.vendorSubmissionModel.value.quotation_request_vendor, 'Finalized', 'vendor-submission-modal');
  }

  getChoiceText(choiceDetails: any[], choiceId: any): string {
    const choice = choiceDetails.find(c => c.id === choiceId);
    return choice ? choice.choice_text : 'N/A';
  }

  async getPPT() {
    this.pptGenService.generateVendorSubmissionPPT(
      this.nzModalData.submissionId,
      this.vendorSubmissionModel.value.quotation_request_vendor_details?.vendor_details?.name,
      this.sectionsData,
      this.vendorSubmissionAnswerReponseModel.value.results
    );
  }

  async getDocX() {
    this.docxGenService.generateVendorSubmissionDocx(
      this.nzModalData.submissionId,
      this.vendorSubmissionModel.value.quotation_request_vendor_details?.vendor_details?.name,
      this.sectionsData,
      this.vendorSubmissionAnswerReponseModel.value.results
    );
  }
}
